import React from 'react';
import { FORM_ERROR } from 'final-form';
import { Link, RouteComponentProps } from '@gatsbyjs/reach-router';
import { Helmet as MetaTags } from 'react-helmet';
import { navigate } from '@gatsbyjs/reach-router';

import Amplify from '~/amplify';
import AuthNav from '~/components/page/Auth/components/AuthNav';
import AuthWrapperHOC from '~/components/page/Auth/components/AuthWrapperHOC';

import ErrorTypes from '~/ErrorTypes';
import TEST_ID from './index.testid';
import Catalog from '~/Catalog';
import { reporter } from '~/hooks/useErrorReporter';
import validate from './utils/validate';
import Button from '~/components/atom/Button';
import { Body, Heading1 } from '~/components/atom/Typography';
import FormUtils from '~/components/organism/FormUtils';
import { EmailField, Form } from '~/components/organism/Forms';
import Input from '~/components/molecule/Input';
import JustificationContainer from '~/components/atom/JustificationContainer';
import AuthFormWrapper from '../components/AuthFormWrapper';

export type FormData = {
  email: string | null;
};

type Props = RouteComponentProps;
const text = {
  emailLabel: Catalog.emailLabel,

  userNotFound: 'Er is nog geen account met dit e-mailadres aangemaakt.',
  explanation:
    'Om je wachtwoord te resetten, hebben we je e-mailadres nodig. Je ontvangt een code waarmee je je wachtwoord kan resetten.',
  genericErrorMessage: Catalog.genericUnknownErrorMessage,
  pageTitle: 'Reset wachtwoord',
  sendEmailButton: 'Verstuur reset e-mail',
  noAccount: 'Nog geen account?',
  registerHere: 'Registreer je hier',
  incompleteUser: 'Dit account is nog niet geverifieerd.',
  inactiveUser:
    'Een reset van je wachtwoord is niet mogelijk. Controleer of je een uitnodiging voor het activeren van je account hebt ontvangen.',
};
const ForgotPassword: React.FCC<Props> = () => {
  const onFormSubmit = ({ email }: { email: string }) => {
    const loweredEmail = email.toLowerCase();

    return Amplify.Auth.forgotPassword(loweredEmail)
      .then(() => {
        void navigate('/setup-newpassword', {
          state: { email: loweredEmail },
        });
      })
      .catch(err => {
        if (
          err.__type === ErrorTypes.notAuthorized ||
          err.code === ErrorTypes.notAuthorized
        ) {
          return { [FORM_ERROR]: text.inactiveUser };
        }

        if (
          err.__type === 'UserNotFoundException' ||
          err.code === 'UserNotFoundException'
        ) {
          return { [FORM_ERROR]: text.userNotFound };
        }

        if (
          err.__type === 'InvalidParameterException' ||
          err.code === 'InvalidParameterException'
        ) {
          return { [FORM_ERROR]: text.incompleteUser };
        }

        reporter.captureException(err.message);
        return {
          [FORM_ERROR]: text.genericErrorMessage,
        };
      });
  };

  const title = text.pageTitle;

  return (
    <AuthWrapperHOC>
      <MetaTags>
        <title>{title}</title>
        <meta name="description" content="Vraag een nieuw wachtwoord aan" />
      </MetaTags>

      <AuthNav selectedIdx={0} showBackLink />

      <AuthFormWrapper>
        <Heading1
          lineHeight="base"
          margin={['l', null]}
          color={{ group: 'primary' }}
          size="xxxl"
        >
          {title}
        </Heading1>

        <p>{text.explanation}</p>
        <Form
          initialValues={initialValues}
          onSubmit={onFormSubmit}
          validate={validate}
        >
          {({ handleSubmit, submitError, submitting, pristine }) => (
            <form
              id="forgotPassword"
              onSubmit={handleSubmit}
              data-testid={TEST_ID.FORM}
            >
              {submitError ? (
                <Body
                  size="base"
                  withoutMargin
                  color={{ group: 'danger', variant: 'light' }}
                  data-testid={TEST_ID.ERROR_MESSAGE}
                >
                  {submitError}
                </Body>
              ) : null}
              <JustificationContainer margin={['m', null]}>
                <EmailField name="email">
                  {({ input, meta: { error, touched } }) => (
                    <Input
                      width="100%"
                      size="large"
                      label={text.emailLabel}
                      error={
                        error && touched
                          ? [FormUtils.showError(error, touched)]
                          : undefined
                      }
                      disabled={submitting}
                      {...input}
                    />
                  )}
                </EmailField>
              </JustificationContainer>
              <JustificationContainer
                align="center"
                justification="space-between"
              >
                <Body align="center" size="base" withoutMargin>
                  {`${text.noAccount} `}
                  <Link to={`/register/${location.search}`}>
                    {text.registerHere}
                  </Link>
                </Body>
                <Button
                  size="large"
                  appearance="secondary"
                  type="submit"
                  disabled={submitting || pristine}
                  label={text.sendEmailButton}
                  dataTestId={TEST_ID.SUBMIT_BUTTON}
                />
              </JustificationContainer>
            </form>
          )}
        </Form>
      </AuthFormWrapper>
    </AuthWrapperHOC>
  );
};

const initialValues: FormData = {
  email: null,
};

export default ForgotPassword;
